import { useCallback, useEffect, useState } from 'react';
import {
  AddMedicine, Confirm, DataTable, PhotoField, toast,
  type Column, type ConsentState, type Destructive, type MedicineParse, type MedicineTransport, type PhotoUploader,
} from '@drbell/ui';
import {
  api, ApiError,
  type MedicationEntry, type Resident, type Schedule,
} from '../api.ts';
import { courseDates, localDateAt } from '@drbell/core';

/**
 * A resident's medicines — RX-ORG-15.
 *
 * The gap this closes is not a small one. The API has had full medication
 * CRUD since the beginning and no console ever reached it, so the only way a
 * medicine could enter DrBell was through the patient's own phone. An
 * 89-year-old in a care home does not have one. A home could therefore admit
 * residents, roster nurses and stare at a rota that could never show an
 * outstanding dose, because no dose could ever exist.
 *
 * Entry is by prescription shorthand — `1-0-1 A/F x 7d` — because that is
 * what is actually written on the slip in front of the nurse, and retyping it
 * into four dropdowns is where transcription errors come from. The parse is
 * NEVER applied silently: it is shown back in words and confirmed, for the
 * same reason the OCR path is. Guessing wrong about a dose is not an
 * acceptable failure.
 *
 * The medicine's NAME is typed, never parsed and never translated. DrBell
 * reminds people of what a doctor prescribed; it does not interpret it.
 */
const FOOD_WORDS: Record<Schedule['foodRelation'], string> = {
  before_food: 'before food',
  with_food: 'with food',
  after_food: 'after food',
  empty_stomach: 'on an empty stomach',
  any: 'any time',
};

const MEAL_WORDS: Record<string, string> = {
  breakfast: 'breakfast', lunch: 'lunch', dinner: 'dinner', bedtime: 'bedtime',
};

/**
 * The offset each food relation already implies.
 *
 * Mirrors `FOOD_OFFSET_MINUTES` in @drbell/core, which is what the parser
 * uses to build the anchors. Without this, an anchor generated *from*
 * "after food" was rendered as "30 min after breakfast ... after food" — the
 * same instruction stated twice, in numbers the prescription never contained,
 * and different from the sentence the nurse confirmed a moment earlier. Two
 * descriptions of one schedule that do not match is how somebody stops
 * trusting both.
 */
const IMPLIED_OFFSET: Record<Schedule['foodRelation'], number> = {
  before_food: -30,
  with_food: 0,
  after_food: 30,
  empty_stomach: -60,
  any: 0,
};

/**
 * What a schedule means, in a sentence a nurse can check against the slip.
 * A dose tied to one of the resident's own times of day is named by that
 * time's name, as typed (`own`, keyed `time:<id>`).
 */
function describe(s: Schedule, own: Record<string, string> = {}): string {
  const food = s.foodRelation === 'any' ? '' : `, ${FOOD_WORDS[s.foodRelation]}`;
  switch (s.type) {
    case 'fixed_times':
      return `${(s.times ?? []).join(', ')}${food}`;
    case 'meal_relative': {
      const implied = IMPLIED_OFFSET[s.foodRelation];
      const parts = (s.anchors ?? []).map((a) => {
        const when = MEAL_WORDS[a.meal] ?? own[a.meal] ?? 'a time of day since removed';
        // When the offset is exactly what the food relation already says,
        // name the meal and let the food phrase carry the timing.
        if (a.offsetMinutes === implied) return `at ${when}`;
        if (a.offsetMinutes === 0) return `with ${when}`;
        const mins = Math.abs(a.offsetMinutes);
        return `${mins} min ${a.offsetMinutes < 0 ? 'before' : 'after'} ${when}`;
      });
      return `${parts.join(', ')}${food}`;
    }
    case 'interval':
      return `every ${s.intervalHours} hours from ${s.anchorTime ?? '—'}${food}`;
    case 'prn':
      return `only when needed${food}`;
  }
}

export function Medicines({ resident, onBack }: { resident: Resident; onBack: () => void }) {
  const [rows, setRows] = useState<MedicationEntry[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [pending, setPending] = useState<Destructive | null>(null);
  const [photoFor, setPhotoFor] = useState<MedicationEntry | null>(null);
  const [stockFor, setStockFor] = useState<MedicationEntry | null>(null);
  const [courseFor, setCourseFor] = useState<MedicationEntry | null>(null);
  const [search, setSearch] = useState('');
  const [ownNames, setOwnNames] = useState<Record<string, string>>({});
  const [slips, setSlips] = useState<Array<{ id: string; url: string; doctorName: string | null; createdAt: number }>>([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await api.get<{ medications: MedicationEntry[] }>(
        `/api/patients/${resident.id}/medications`,
      );
      setRows(d.medications);
      // Their own times of day, to name the doses tied to them.
      const own = await api
        .get<{ routineTimes: Array<{ id: string; name: string }> }>(`/api/patients/${resident.id}/routine-times`)
        .catch(() => ({ routineTimes: [] }));
      setOwnNames(Object.fromEntries(own.routineTimes.map((t) => [`time:${t.id}`, t.name])));
      // The prescription photographs — defect 138: uploaded, and until now
      // never kept anywhere anybody could see them.
      const rx = await api
        .get<{ prescriptions: Array<{ id: string; url: string; doctorName: string | null; createdAt: number }> }>(
          `/api/patients/${resident.id}/prescriptions`,
        )
        .catch(() => ({ prescriptions: [] }));
      setSlips(rx.prescriptions);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
      setRows(null);
    } finally {
      setLoading(false);
    }
  }, [resident.id]);

  useEffect(() => { void load(); }, [load]);

  const q = search.trim().toLowerCase();
  const filtered = rows?.filter((r) =>
    !q ||
    r.medication.name.toLowerCase().includes(q) ||
    (r.medication.brandName ?? '').toLowerCase().includes(q),
  ) ?? null;

  const columns: Column<MedicationEntry>[] = [
    {
      key: 'name',
      header: 'Medicine',
      cell: (r) => (
        <div className="min-w-0">
          <div className="font-medium">
            {r.medication.name}
            {r.medication.strength && (
              <span className="ml-1 text-(--muted)">
                {r.medication.strength}{r.medication.strengthUnit ?? ''}
              </span>
            )}
          </div>
          {/* The second label the family added, never derived from the first.
              Shown after the packet name because the packet name is what
              matches the strip in the drawer. */}
          {r.medication.nameAlt && (
            <div className="text-xs text-(--muted)">{r.medication.nameAlt}</div>
          )}
        </div>
      ),
    },
    {
      key: 'when',
      header: 'When',
      cell: (r) =>
        r.schedules.filter((s) => !s.archivedAt).length === 0 ? (
          <span className="text-(--muted)">no schedule</span>
        ) : (
          <div className="space-y-0.5">
            {r.schedules.filter((s) => !s.archivedAt).map((s) => (
              <div key={s.id} className="text-sm">{describe(s, ownNames)}</div>
            ))}
          </div>
        ),
    },
    {
      key: 'photo',
      header: 'Strip',
      width: '5rem',
      cell: (r) =>
        r.medication.stripImageKey ? (
          <img
            src={`/api/media/${r.medication.stripImageKey}`}
            alt={`Strip of ${r.medication.name}`}
            loading="lazy"
            className="h-12 w-12 rounded-lg border border-(--line) object-cover"
          />
        ) : (
          <button
            onClick={() => setPhotoFor(r)}
            title="Add the photo that appears on the reminder"
            className="min-h-9 rounded-lg border border-dashed border-(--line) px-2 text-xs text-(--muted) hover:border-(--accent)"
          >
            + photo
          </button>
        ),
    },
    {
      key: 'critical',
      header: '',
      width: '7rem',
      cell: (r) =>
        r.medication.isCritical ? (
          <span
            title="A missed dose escalates immediately rather than waiting."
            className="rounded-full border border-red-500/40 px-2 py-0.5 text-xs text-red-500"
          >
            critical
          </span>
        ) : null,
    },
    {
      key: 'stock',
      header: 'In the drawer',
      optional: true,
      numeric: true,
      /*
       * A button, not a number.
       *
       * The count is what the refill reminder is computed from, and nothing
       * could ever set it — so the reminder either never fired or fired for
       * ever. Making the cell the way to correct it means the number and the
       * means of fixing it are in the same place.
       */
      cell: (r) => (
        <button
          onClick={() => setStockFor(r)}
          title="Record a refill, or correct the count"
          className="min-h-9 rounded-lg border border-(--line) px-2 text-xs tabular-nums hover:border-(--accent)"
        >
          {r.medication.stockCount === null ? 'not counted' : `${r.medication.stockCount}`}
        </button>
      ),
    },
    {
      key: 'course',
      header: 'Course',
      // How long, as the doctor said — and the way to record a change to it.
      cell: (r) => {
        const live = r.schedules.filter((sc) => sc.archivedAt === null);
        if (live.length === 0) return null;
        const end = live[0]!.endDate;
        return (
          <button
            onClick={() => setCourseFor(r)}
            title="Record a change the doctor made to how long to take it"
            className="min-h-9 rounded-lg border border-(--line) px-2 text-xs hover:border-(--accent)"
          >
            {end ? `until ${dayName(end)}` : 'no end'}
          </button>
        );
      },
    },
    {
      key: 'prescribedBy',
      header: 'Prescribed by',
      optional: true,
      cell: (r) => r.medication.prescribedBy ?? <span className="text-(--muted)">—</span>,
    },
    {
      key: 'actions',
      header: '',
      width: '7rem',
      cell: (r) => (
        <div className="flex justify-end">
          <button
            onClick={() =>
              setPending({
                title: `Stop ${r.medication.name}?`,
                /*
                 * "Stop", not "delete", and the body says why: the record is
                 * archived rather than removed. A course that was stopped is
                 * still part of what happened to this person, and a home that
                 * is asked "was she on it in March?" must be able to answer.
                 */
                body: 'No further reminders are created for it, from now. Every dose already recorded stays in the record exactly as it is.',
                reassurance: 'Nothing is erased. The medicine and its history remain readable in exports and handovers.',
                confirmLabel: 'Stop this medicine',
                onConfirm: async () => {
                  await api.del(`/api/patients/${resident.id}/medications/${r.medication.id}`);
                  toast.success(
                    `${r.medication.name} stopped`,
                    'No further reminders. Everything already recorded is kept.',
                  );
                  await load();
                },
              })
            }
            className="min-h-9 rounded-lg border border-(--line) px-2 text-xs hover:border-red-500"
          >
            Stop
          </button>
        </div>
      ),
    },
  ];

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <button
          onClick={onBack}
          className="min-h-11 rounded-lg border border-(--line) px-3 text-sm hover:border-(--accent)"
        >
          ← Residents
        </button>
        <h2 className="text-lg font-semibold">
          {resident.displayName}
          {resident.locationNote && (
            <span className="ml-2 font-normal text-(--muted)">{resident.locationNote}</span>
          )}
        </h2>
        <button
          onClick={() => setAdding(true)}
          className="ml-auto min-h-11 rounded-lg bg-(--accent) px-4 text-sm font-medium text-white"
        >
          Add a medicine
        </button>
      </div>

      <DataTable
        columns={columns}
        rows={filtered}
        rowKey={(r) => r.medication.id}
        loading={loading}
        error={error}
        search={search}
        onSearch={setSearch}
        searchPlaceholder="Search by name or brand…"
        noun="medicines"
        storageKey="org-medicines"
        emptyAction={
          <button
            onClick={() => setAdding(true)}
            className="min-h-11 rounded-lg bg-(--accent) px-4 text-sm font-medium text-white"
          >
            Add the first one
          </button>
        }
      />

      {slips.length > 0 && (
        <div className="rounded-xl border border-(--line) p-4">
          <h3 className="mb-2 text-sm font-medium">Prescriptions</h3>
          <ul className="flex flex-wrap gap-3">
            {slips.map((p) => (
              <li key={p.id}>
                <a href={p.url} target="_blank" rel="noopener" className="block">
                  <img src={p.url} alt={`Prescription${p.doctorName ? ` from ${p.doctorName}` : ''}`} className="h-24 w-24 rounded-lg border border-(--line) object-cover" />
                </a>
                <p className="mt-1 max-w-24 truncate text-xs text-(--muted)">
                  {p.doctorName ?? new Date(p.createdAt).toLocaleDateString()}
                </p>
                {(
                  <button
                    onClick={() =>
                      setPending({
                        title: 'Remove this prescription photo?',
                        body: 'The photograph is deleted. The medicines added with it are not affected.',
                        confirmLabel: 'Remove photo',
                        onConfirm: async () => {
                          setPending(null);
                          try {
                            await api.del(`/api/patients/${resident.id}/prescriptions/${p.id}`);
                            toast.success('Photo removed');
                            await load();
                          } catch (e) {
                            toast.error('Could not remove it', e instanceof Error ? e.message : String(e));
                          }
                        },
                      })
                    }
                    className="text-xs text-(--muted) hover:text-red-500"
                  >
                    Remove
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      <p className="text-xs text-(--muted)">
        DrBell reminds people of what their doctor prescribed. It never suggests
        a medicine or a dose, and it never translates the name of one — a
        translated medicine name may name nothing that exists.
      </p>

      {adding && (
        <AddMedicine
          personName={resident.displayName}
          transport={medicineTransport(resident)}
          onClose={() => setAdding(false)}
          onAdded={async () => { setAdding(false); await load(); }}
        />
      )}

      {courseFor && (
        <CourseDialog
          resident={resident}
          entry={courseFor}
          onClose={() => setCourseFor(null)}
          onSaved={async () => { setCourseFor(null); await load(); }}
        />
      )}

      {stockFor && (
        <StockDialog
          resident={resident}
          entry={stockFor}
          onClose={() => setStockFor(null)}
          onSaved={async () => { setStockFor(null); await load(); }}
        />
      )}

      {photoFor && (
        <PhotoDialog
          resident={resident}
          entry={photoFor}
          onClose={() => setPhotoFor(null)}
          onSaved={async () => { setPhotoFor(null); await load(); }}
        />
      )}

      {pending && <Confirm {...pending} onCancel={() => setPending(null)} />}
    </section>
  );
}

/**
 * Attaching the photo afterwards.
 *
 * Which is when it actually happens. The slip is in one hand when the
 * medicine is entered and the box is in a drawer down the corridor, so a
 * photograph that can only be added at creation is one that never gets added.
 */
function PhotoDialog({ resident, entry, onClose, onSaved }: {
  resident: Resident;
  entry: MedicationEntry;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const [strip, setStrip] = useState<{ key: string; url: string } | null>(
    entry.medication.stripImageKey
      ? { key: entry.medication.stripImageKey, url: `/api/media/${entry.medication.stripImageKey}` }
      : null,
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" aria-labelledby="medicine-photo-title">
      <div className="w-full max-w-md rounded-2xl border border-(--line) bg-(--bg) p-5">
        <h2 id="medicine-photo-title" className="text-lg font-semibold">Photo for {entry.medication.name}</h2>
        <p className="mt-1 text-sm text-(--muted)">
          Photograph the strip as it looks in the drawer. It appears on the
          reminder, so {resident.displayName} can check what is in their hand
          against what is on the screen.
        </p>

        <div className="mt-4">
          <PhotoField
            label="Photo of the strip"
            value={strip}
            uploader={uploaderFor(resident.id, 'strip')}
            onChange={setStrip}
          />
        </div>

        {error && (
          <p role="alert" className="mt-3 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-500">
            {error}
          </p>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="min-h-11 rounded-lg border border-(--line) px-4 text-sm">
            Cancel
          </button>
          <button
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                await api.put(
                  `/api/patients/${resident.id}/medications/${entry.medication.id}`,
                  { stripImageKey: strip?.key ?? null },
                );
                toast.success(
                  strip ? 'Photo saved' : 'Photo removed',
                  strip ? 'It now appears on the reminder.' : undefined,
                );
                await onSaved();
              } catch (e) {
                setError(e instanceof ApiError ? e.message : String(e));
                setBusy(false);
              }
            }}
            className="min-h-11 rounded-lg bg-(--accent) px-4 text-sm font-medium text-white disabled:opacity-40"
          >
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Uploads to this resident's media, and hands back the key to store. */
const uploaderFor = (patientId: string, kind: 'strip' | 'pill' | 'prescription'): PhotoUploader => ({
  upload: (file) =>
    api.upload<{ key: string; url: string }>(
      `/api/patients/${patientId}/media?kind=${kind}`,
      file,
    ),
});

/** The shared "Add a medicine" dialog, talking to this console's client. */
const medicineTransport = (resident: Resident): MedicineTransport => ({
  parse: (input, unit) => api.post<MedicineParse>('/api/notation/parse', { input, unit: unit ?? 'tablet' }),
  create: (body) => api.post(`/api/patients/${resident.id}/medications`, body),
  consent: () => api.get<ConsentState>('/api/consent'),
  acceptConsent: (documentId, version) => api.post('/api/consent/accept', { documentId, version }),
  uploader: (kind) => uploaderFor(resident.id, kind),
  // The resident's dates, and an inclusive end — defect 136.
  courseDates: (days) => courseDates(days, resident.timezone),
});

/** "Sat 26 Sep", for a calendar date — no time zone involved. */
const dayName = (d: string) =>
  new Date(`${d}T00:00:00Z`).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });

/**
 * The doctor changed how long to take it — RX-MED-12.
 *
 * The new last day (included) or no end, and what the doctor said. Future
 * reminders are rebuilt; what was recorded is kept. A day already past is
 * refused — that is stopping.
 */
function CourseDialog({ resident, entry, onClose, onSaved }: {
  resident: Resident;
  entry: MedicationEntry;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const current = entry.schedules.find((sc) => sc.archivedAt === null)?.endDate ?? null;
  const today = localDateAt(Date.now(), resident.timezone);
  const [lastDay, setLastDay] = useState(current && current >= today ? current : today);
  const [noEnd, setNoEnd] = useState(current === null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const next = noEnd ? null : lastDay;
  const valid = noEnd || (/^\d{4}-\d{2}-\d{2}$/.test(lastDay) && lastDay >= today);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.put(`/api/patients/${resident.id}/medications/${entry.medication.id}/course`, {
        endDate: next,
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      toast.success(
        `${entry.medication.name}: ${next ? `until ${dayName(next)}` : 'no end date'}`,
        'Future reminders rebuilt. What was recorded is kept.',
      );
      await onSaved();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" aria-labelledby="medicine-course-title">
      <div className="w-full max-w-md rounded-2xl border border-(--line) bg-(--bg) p-5">
        <h2 id="medicine-course-title" className="text-lg font-semibold">How long to take {entry.medication.name}</h2>
        <p className="mt-1 text-sm text-(--muted)">
          For {resident.displayName}. Currently {current ? `until ${dayName(current)}` : 'no end date'}. Only change
          this when the doctor has.
        </p>
        <label className="mt-4 flex min-h-11 items-center gap-2 text-sm">
          <input type="radio" checked={!noEnd} onChange={() => setNoEnd(false)} />
          <span>Last day</span>
          <input
            type="date"
            value={lastDay}
            min={today}
            disabled={noEnd}
            onChange={(e) => setLastDay(e.target.value)}
            className="rounded-lg border border-(--line) bg-(--panel) px-2 py-1 disabled:opacity-40"
          />
        </label>
        <label className="flex min-h-11 items-center gap-2 text-sm">
          <input type="radio" checked={noEnd} onChange={() => setNoEnd(true)} />
          <span>No end — until the doctor says to stop</span>
        </label>
        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          maxLength={300}
          placeholder="What the doctor said (optional)"
          aria-label="What the doctor said"
          className="mt-2 min-h-11 w-full rounded-lg border border-(--line) bg-(--panel) px-3"
        />
        {error && <p role="alert" className="mt-3 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-500">{error}</p>}
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="min-h-11 rounded-lg border border-(--line) px-4 text-sm">Cancel</button>
          <button
            disabled={!valid || busy || next === current}
            onClick={() => void save()}
            className="min-h-11 rounded-lg bg-(--accent) px-4 text-sm font-medium text-white disabled:opacity-40"
          >
            {busy ? 'Saving…' : 'Record the change'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * How many are left, and how few is too few — RX-MED-11.
 *
 * Two modes, because "I bought 30 more" and "there are 12 left" are different
 * statements and making somebody do the arithmetic to express one as the
 * other is where a count starts drifting from the drawer.
 *
 * The threshold is in DAYS, not tablets. Ten tablets is a fortnight of one a
 * day and three days of three a day, so a tablet count cannot mean "warn me
 * in time" — only a day count can, and the server works the tablets out from
 * the schedule.
 */
function StockDialog({ resident, entry, onClose, onSaved }: {
  resident: Resident;
  entry: MedicationEntry;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const [mode, setMode] = useState<'set' | 'add'>('set');
  const [count, setCount] = useState('');
  const [days, setDays] = useState('7');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const n = Number(count);
  const valid = /^\d+(\.\d+)?$/.test(count.trim()) && n >= 0;

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" aria-labelledby="medicine-detail-title">
      <div className="w-full max-w-md rounded-2xl border border-(--line) bg-(--bg) p-5">
        <h2 id="medicine-detail-title" className="text-lg font-semibold">{entry.medication.name}</h2>
        <p className="mt-1 text-sm text-(--muted)">
          {entry.medication.stockCount === null
            ? 'Nothing has been counted yet, so no refill reminder can be worked out.'
            : `DrBell believes there are ${entry.medication.stockCount} left.`}
        </p>

        <div className="mt-4 flex gap-2">
          {([['set', 'There are this many left'], ['add', 'I bought this many more']] as const).map(
            ([value, label]) => (
              <button
                key={value}
                onClick={() => setMode(value)}
                aria-pressed={mode === value}
                className={`min-h-11 flex-1 rounded-lg border px-3 text-sm ${
                  mode === value
                    ? 'border-(--accent) bg-(--accent-soft) text-(--accent)'
                    : 'border-(--line) hover:border-(--accent)'
                }`}
              >
                {label}
              </button>
            ),
          )}
        </div>

        <label className="mt-3 block">
          <span className="text-xs text-(--muted)">
            {mode === 'set' ? 'Count what is in the drawer' : 'How many were bought'}
          </span>
          <input
            value={count}
            onChange={(e) => setCount(e.target.value)}
            inputMode="decimal"
            autoFocus
            placeholder="30"
            className="mt-1 min-h-11 w-full rounded-lg border border-(--line) bg-(--panel) px-3"
          />
        </label>

        <label className="mt-3 block">
          <span className="text-xs text-(--muted)">Warn when this many days are left</span>
          <input
            value={days}
            onChange={(e) => setDays(e.target.value.replace(/\D/g, '').slice(0, 2))}
            inputMode="numeric"
            className="mt-1 min-h-11 w-full rounded-lg border border-(--line) bg-(--panel) px-3"
          />
          <span className="mt-1 block text-xs text-(--muted)">
            Days, not tablets — ten tablets is a fortnight of one a day and
            three days of three a day. DrBell works out the rest from the
            schedule.
          </span>
        </label>

        {error && (
          <p role="alert" className="mt-3 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-500">
            {error}
          </p>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="min-h-11 rounded-lg border border-(--line) px-4 text-sm">
            Cancel
          </button>
          <button
            disabled={busy || !valid}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                await api.put(
                  `/api/patients/${resident.id}/medications/${entry.medication.id}/stock`,
                  {
                    mode,
                    count: n,
                    refillThresholdDays: days === '' ? null : Number(days),
                  },
                );
                toast.success(
                  mode === 'set' ? 'Count corrected' : `${n} added`,
                  'The refill reminder is worked out from this.',
                );
                await onSaved();
              } catch (e) {
                setError(e instanceof ApiError ? e.message : String(e));
                setBusy(false);
              }
            }}
            className="min-h-11 rounded-lg bg-(--accent) px-4 text-sm font-medium text-white disabled:opacity-40"
          >
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
